<template>
  <div class="rb-auth-layout rb-ob-layout">
    <div class="rb-auth-shell">
      <header class="rb-auth-top">
        <button
            type="button"
            class="rb-auth-icon-btn rb-ob-back-btn"
            :aria-label="$t('authUi.back')"
            @click="goBack"
        >
          <span aria-hidden="true">›</span>
          <span class="rb-ob-back-label">{{ $t('authUi.back') }}</span>
        </button>
        <div class="rb-ob-nav-logo" :aria-label="$t('authUi.brand')">
          <img src="../../assets/logo/roobroo-wordmark.svg" alt="">
        </div>
        <span class="rb-auth-icon-btn rb-ob-nav-spacer" aria-hidden="true"></span>
      </header>
      <main class="rb-auth-main">
        <router-view/>
      </main>
    </div>
  </div>
</template>

<script>
import {loadIntake, clearIntakePending} from '@/intake/intakeState'
import {scrollIntakeToTop, focusIntakeStepTitle} from '@/intake/intakeUx'
import {EventBus} from '@/event-bus'

/** Back targets for V2-backend-only intake routes (static draft chain removed). */
const BACK_ROUTE = {
  intakeCrisis: 'intakeScreening',
  intakeDisp: 'intakeScreening',
  intakeMatching: 'intakeScreening',
  intakeMatches: 'intakeScreening',
  intakeTherapist: 'intakeMatches',
  intakeSlots: 'intakeTherapist',
  intakeHold: 'intakeSlots',
  intakeConflict: 'intakeSlots',
  intakeHoldExpired: 'intakeSlots',
  intakeCheckout: 'intakeSlots',
  intakePayReturn: 'intakeCheckout',
  intakePayProcessing: 'intakeCheckout',
  intakePaySuccess: 'intakeCheckout',
  intakePayFail: 'intakeCheckout',
  intakePayMismatch: 'intakeCheckout',
  intakePayLate: 'intakeCheckout',
  intakeConsent: 'intakePaySuccess',
  intakeBaseline: 'intakeConsent',
  intakeLetter: 'intakeBaseline',
  intakeLetterVoice: 'intakeLetter',
  intakeClientHome: 'intakeConsent',
  intakeReadyLock: 'intakeClientHome',
  intakeReadyOk: 'intakeClientHome',
  intakeSessionCheck: 'intakeReadyOk',
  intakeSessionAvCheck: 'intakeReadyOk',
  intakeSessionWait: 'intakeSessionAvCheck',
  intakeSessionRoom: 'intakeSessionWait',
  intakeSessionReconnect: 'intakeSessionRoom',
  intakeSessionEnd: 'intakeSessionRoom',
  intakeSessionOutcome: 'intakeSessionEnd',
  intakeOutcomeSrs: 'intakeSessionEnd',
  intakeOutcomeOrs: 'intakeAssessMiss',
  intakeAssessDone: 'intakeOutcomeSrs',
  intakeAssessMiss: 'intakeOutcomeOrs',
  intakeThStep1: 'intakeClientHome',
  intakeTh03: 'intakeThStep1',
  intakeQm01: 'intakeQmStep2',
  intakeQmStep2: 'intakeThStep1',
  intakeNotify: 'intakeClientHome',
  intakeConsentSigner: 'intakeClientHome',
  intakeConsentGuard: 'intakeConsentSigner',
  intakeMatchExpired: 'intakeMatches',
  intakeMatchGap: 'intakeMatching',
  intakeMatchInvalid: 'intakeMatches',
  intakeMatchNoslot: 'intakeTherapist',
  intakeRematchOffer: 'intakeMatches',
  intakeRematchDecide: 'intakeRematchOffer',
  intakeRematchRun: 'intakeRematchDecide',
  intakeRematchFail: 'intakeRematchRun',
  intakeSessionReschedule: 'dashboard',
  intakeSessionCancel: 'dashboard',
  intakeBkT02: 'dashboard',
  intakeBkT03: 'dashboard',
  intakeBkT04: 'dashboard',
  intakeBkTOnce: 'dashboard',
  intakeBkS01: 'intakeBkS02',
  intakeBkS02: 'dashboard',
  intakeBkS03: 'intakeBkS02',
}

export default {
  name: 'IntakeView',
  watch: {
    $route() {
      this.$nextTick(() => {
        scrollIntakeToTop(this.$el)
        focusIntakeStepTitle(this.$el)
      })
    },
  },
  mounted() {
    // One-shot post-auth redirect into intake — clear so guest routes stop forcing here
    clearIntakePending()
    this.$nextTick(() => {
      scrollIntakeToTop(this.$el)
      focusIntakeStepTitle(this.$el)
    })
  },
  methods: {
    goBack() {
      const name = this.$route.name
      if (name === 'intakeScreening') {
        EventBus.$emit('intake-screening-back')
        return
      }
      if (name === 'intakeDisp' && this.$route.params.kind === 'specialist') {
        EventBus.$emit('intake-specialist-review')
        return
      }
      if (name === 'intakeSlots') {
        const therapistId = this.$route.params.therapistId || loadIntake().therapistId
        this.$router.push({
          name: 'intakeTherapist',
          params: {
            lang: this.$route.params.lang || 'fa',
            therapistId,
          },
        })
        return
      }
      if (name === 'intakeTherapist') {
        this.$router.push({
          name: 'intakeMatches',
          params: {lang: this.$route.params.lang || 'fa'},
        })
        return
      }
      if (name === 'intakeMatchNoslot') {
        const therapistId = loadIntake().therapistId
        if (therapistId) {
          this.$router.push({
            name: 'intakeTherapist',
            params: {
              lang: this.$route.params.lang || 'fa',
              therapistId,
            },
          })
          return
        }
      }
      if (name === 'intakeHold' || name === 'intakeConflict' || name === 'intakeHoldExpired') {
        const draft = loadIntake()
        const therapistId = draft.therapistId || this.$route.params.therapistId
        if (therapistId) {
          this.$router.push({
            name: 'intakeSlots',
            params: {
              lang: this.$route.params.lang || 'fa',
              therapistId,
            },
          })
          return
        }
      }
      if (name === 'intakeCheckout') {
        this.$router.push({
          name: 'intakeHold',
          params: {lang: this.$route.params.lang || 'fa'},
        })
        return
      }
      const target = BACK_ROUTE[name]
      if (target === 'dashboard') {
        this.$router.push({
          name: 'dashboard',
          params: {lang: this.$route.params.lang || 'fa'},
        })
        return
      }
      if (target) {
        this.$router.push({
          name: target,
          params: {lang: this.$route.params.lang || 'fa'},
          query: Object.assign({}, this.$route.query),
        })
        return
      }
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push({name: 'portalHome', params: {lang: this.$route.params.lang || 'fa'}})
      }
    },
  },
}
</script>
